
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import "../../src/styles/RoleAssign.css";
import { Button } from "react-bootstrap";
import DashboardLayout from "../../src/components/DashboardLayout";

function RoleAssign() {
    const [roles, setRoles] = useState([]);
    const [menus, setMenus] = useState([]);
    const [selectedRole, setSelectedRole] = useState("");
    const [selectedMenus, setSelectedMenus] = useState([]);
    const [expandedParents, setExpandedParents] = useState([]);
    const [error, setError] = useState("");

    // Ref object to store refs for all parent checkboxes
    const parentCheckboxRefs = useRef({});

    // Fetch roles and menus on component mount
    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const response = await axios.get(
                    "/api/roles/getall",
                );
                setRoles(response.data);
            } catch (err) {
                console.error("Failed to fetch roles", err);
                setError("Unable to load roles.");
            }
        };

        const fetchMenus = async () => {
            try {
                const response = await axios.get(
                    "/api/menus/getall",
                );
                setMenus(response.data);
            } catch (err) {
                console.error("Failed to fetch menus", err);
                setError("Unable to load menus.");
            }
        };

        fetchRoles();
        fetchMenus();
    }, []);

    // Fetch assigned menus when selectedRole changes, and initialize selectedMenus with assigned menus
    useEffect(() => {
        if (!selectedRole) {
            setSelectedMenus([]);
            return;
        }

        const fetchAssignedMenus = async () => {
            try {
                const response = await axios.get(
                    `/api/menus/assigned/${selectedRole}`,
                );
                const assigned = response.data.map((id) => String(id));
                setSelectedMenus(assigned);
            } catch (error) {
                console.error("Failed to fetch assigned menus", error);
                setSelectedMenus([]);
            }
        };

        fetchAssignedMenus();
    }, [selectedRole]);

    // Update indeterminate state of parent checkboxes after render
    useEffect(() => {
        menus
            .filter(
                (menu) =>
                    menu.parentmenuid === null ||
                    menu.parentmenuid === "0" ||
                    menu.parentmenuid === "",
            )
            .forEach((parentMenu) => {
                const childMenus = menus.filter(
                    (menu) => String(menu.parentmenuid) === String(parentMenu.id),
                );
                const isParentSelected = selectedMenus.includes(String(parentMenu.id));
                const areAllChildrenSelected =
                    childMenus.length > 0 &&
                    childMenus.every((child) => selectedMenus.includes(String(child.id)));
                const isPartiallySelected = isParentSelected && !areAllChildrenSelected;

                const checkbox = parentCheckboxRefs.current[parentMenu.id];
                if (checkbox) {
                    checkbox.indeterminate = isPartiallySelected;
                }
            });
    }, [menus, selectedMenus]);

    // Expand/collapse parent menus
    const toggleExpand = (menuId) => {
        setExpandedParents((prev) =>
            prev.includes(menuId)
                ? prev.filter((id) => id !== menuId)
                : [...prev, menuId],
        );
    };

    // Toggle child menu selection, update parent menu selection accordingly
    const toggleMenuSelection = (menuId) => {
        const menuIdStr = String(menuId);

        setSelectedMenus((prevSelected) => {
            const isSelected = prevSelected.includes(menuIdStr);
            let newSelected;

            if (isSelected) {
                // Remove this child menu
                newSelected = prevSelected.filter((id) => id !== menuIdStr);
            } else {
                // Add this child menu
                newSelected = [...prevSelected, menuIdStr];
            }

            // Find parent menu ID of this menu
            const menu = menus.find((m) => String(m.id) === menuIdStr);
            const parentIdStr =
                menu && menu.parentmenuid ? String(menu.parentmenuid) : null;

            if (parentIdStr && parentIdStr !== "0" && parentIdStr !== "") {
                if (!isSelected) {
                    // If child selected, add parent menu if not already selected
                    if (!newSelected.includes(parentIdStr)) {
                        newSelected.push(parentIdStr);
                    }
                } else {
                    // If child deselected, check if any sibling still selected
                    const siblingChildIds = menus
                        .filter((m) => String(m.parentmenuid) === parentIdStr)
                        .map((m) => String(m.id));

                    const anySiblingSelected = siblingChildIds.some((id) =>
                        newSelected.includes(id),
                    );

                    if (!anySiblingSelected) {
                        // Remove parent menu if no siblings selected
                        newSelected = newSelected.filter((id) => id !== parentIdStr);
                    }
                }
            }

            return newSelected;
        });
    };

    // Toggle all children selection when parent checkbox toggled
    const handleParentCheckboxToggle = (parentMenu) => {
        const parentIdStr = String(parentMenu.id);
        const childIdsStr = menus
            .filter((menu) => String(menu.parentmenuid) === parentIdStr)
            .map((menu) => String(menu.id));

        const allSelected =
            selectedMenus.includes(parentIdStr) &&
            childIdsStr.every((id) => selectedMenus.includes(id));

        if (allSelected) {
            // Remove parent and all children
            setSelectedMenus((prev) =>
                prev.filter((id) => id !== parentIdStr && !childIdsStr.includes(id)),
            );
        } else {
            // Add parent and all children
            setSelectedMenus((prev) => [
                ...new Set([...prev, parentIdStr, ...childIdsStr]),
            ]);
        }
    };

    const handleRoleChange = (e) => setSelectedRole(e.target.value);

    const assignMenusToRole = async () => {
        if (!selectedRole) {
            alert("Please select a role.");
            return;
        }

        try {
            // Fetch current assigned menus before updating, to compare
            const currentAssignedResponse = await axios.get(
                `/api/menus/assigned/${selectedRole}`,
            );
            const assignedMenus = currentAssignedResponse.data.map((id) =>
                String(id),
            );

            // Menus to assign (selectedMenus but not currently assigned)
            const menusToAssign = selectedMenus.filter(
                (id) => !assignedMenus.includes(id),
            );
            // Menus to remove (currently assigned but unchecked)
            const menusToRemove = assignedMenus.filter(
                (id) => !selectedMenus.includes(id),
            );

            if (menusToAssign.length > 0) {
                await axios.post("/api/menus/assignmenus", {
                    userTypeId: selectedRole,
                    menuIds: menusToAssign,
                });
            }

            if (menusToRemove.length > 0) {
                await axios.post("/api/removemenus", {
                    userTypeId: selectedRole,
                    menuIds: menusToRemove,
                });
            }

            alert("Menu assignments updated successfully!");

            // Refresh selectedMenus after update to reflect latest assignments
            const refreshedResponse = await axios.get(
                `/api/menus/assigned/${selectedRole}`,
            );
            const refreshedAssigned = refreshedResponse.data.map((id) => String(id));
            setSelectedMenus(refreshedAssigned);
        } catch (error) {
            console.error("Error updating menu assignments:", error);
            alert("Failed to update menu assignments.");
        }
    };

    //return (
    //  <div className="container">
    //    <h2 className="mb-4">Assign Role to User</h2>

    //    {error && <p className="error">{error}</p>}

    //    {/* Role Selection */}
    //    <div className="mb-4">
    //      <h5>Select Role :</h5>
    //      <select
    //        value={selectedRole}
    //        onChange={handleRoleChange}
    //        className="form-control"
    //        style={{ width: "200px" }}
    //      >
    //        <option value="">-- Select Role --</option>
    //        {roles.map((role) => (
    //          <option key={role.id} value={role.id}>
    //            {role.type}
    //          </option>
    //        ))}
    //      </select>
    //    </div>

    //    <div>
    //      <h5 className="mb-3">Select Menus</h5>
    //      <div>
    //        {menus
    //          .filter(
    //            (menu) =>
    //              menu.parentmenuid === null ||
    //              menu.parentmenuid === "0" ||
    //              menu.parentmenuid === "",
    //          )
    //          .map((parentMenu) => {
    //            const childMenus = menus.filter(
    //              (menu) => String(menu.parentmenuid) === String(parentMenu.id),
    //            );

    //            const isParentSelected = selectedMenus.includes(
    //              String(parentMenu.id),
    //            );
    //            const areAllChildrenSelected =
    //              childMenus.length > 0 &&
    //              childMenus.every((child) =>
    //                selectedMenus.includes(String(child.id)),
    //              );

    //            return (
    //              <div
    //                key={parentMenu.id}
    //                style={{
    //                  width: "100%",
    //                  marginBottom: "1rem",
    //                  borderBottom: "1px solid #ccc",
    //                  paddingBottom: "0.5rem",
    //                }}
    //              >
    //                <div className="d-flex align-items-center mb-1">
    //                  <input
    //                    type="checkbox"
    //                    ref={(el) =>
    //                      (parentCheckboxRefs.current[parentMenu.id] = el)
    //                    }
    //                    className="me-2"
    //                    checked={areAllChildrenSelected}
    //                    onChange={() => handleParentCheckboxToggle(parentMenu)}
    //                    onClick={(e) => e.stopPropagation()}
    //                  />
    //                  <span
    //                    className="me-2"
    //                    style={{ fontSize: "1.2rem", cursor: "pointer" }}
    //                    onClick={() => toggleExpand(parentMenu.id)}
    //                  >
    //                    {expandedParents.includes(parentMenu.id) ? "▼" : "▶"}
    //                  </span>
    //                  <strong
    //                    style={{
    //                      color:
    //                        isParentSelected && !areAllChildrenSelected
    //                          ? "#999" // partial selection color
    //                          : areAllChildrenSelected
    //                            ? "#333"
    //                            : "#000",
    //                    }}
    //                  >
    //                    {parentMenu.menuName}
    //                  </strong>
    //                </div>

    //                {expandedParents.includes(parentMenu.id) &&
    //                  childMenus.length > 0 && (
    //                    <div style={{ paddingLeft: "2rem" }}>
    //                      {childMenus.map((child) => {
    //                        const isChildSelected = selectedMenus.includes(
    //                          String(child.id),
    //                        );
    //                        return (
    //                          <div
    //                            key={child.id}
    //                            className="d-flex align-items-center mb-1"
    //                          >
    //                            <input
    //                              type="checkbox"
    //                              className="me-2"
    //                              checked={isChildSelected}
    //                              onChange={() => toggleMenuSelection(child.id)}
    //                            />
    //                            <strong>{child.menuName}</strong>
    //                          </div>
    //                        );
    //                      })}
    //                    </div>
    //                  )}
    //              </div>
    //            );
    //          })}
    //      </div>
    //    </div>

    //    <Button
    //      type="button"
    //      className="assignrole-btn"
    //      onClick={assignMenusToRole}
    //    >
    //      Submit
    //    </Button>
    //  </div>
    //);
    return (
        <DashboardLayout>
            <div className="container">
                <h2 className="mb-4">Assign Role to User</h2>

                {error && <p className="error">{error}</p>}

                {/* Role Selection */}
                <div className="mb-4">
                    <h5>Select Role :</h5>
                    <select
                        value={selectedRole}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        className="form-control"
                        style={{ width: "200px" }}
                    >
                        <option value="">-- Select Role --</option>
                        {roles.map((role) => (
                            <option key={role.id} value={role.id}>
                                {role.type}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <h5 className="mb-3">Select Menus</h5>
                    <div>
                        {menus
                            .filter(
                                (menu) =>
                                    menu.parentmenuid === null ||
                                    menu.parentmenuid === "0" ||
                                    menu.parentmenuid === ""
                            )
                            .map((parentMenu) => {
                                const childMenus = menus.filter(
                                    (menu) => String(menu.parentmenuid) === String(parentMenu.id)
                                );

                                const isParentSelected = selectedMenus.includes(
                                    String(parentMenu.id)
                                );
                                const areAllChildrenSelected =
                                    childMenus.length > 0 &&
                                    childMenus.every((child) =>
                                        selectedMenus.includes(String(child.id))
                                    );

                                return (
                                    <div
                                        key={parentMenu.id}
                                        style={{
                                            width: "100%",
                                            marginBottom: "1rem",
                                            borderBottom: "1px solid #ccc",
                                            paddingBottom: "0.5rem",
                                        }}
                                    >
                                        <div className="d-flex align-items-center mb-1">
                                            <input
                                                type="checkbox"
                                                ref={(el) =>
                                                    (parentCheckboxRefs.current[parentMenu.id] = el)
                                                }
                                                className="me-2"
                                                checked={areAllChildrenSelected}
                                                onChange={() => handleParentCheckboxToggle(parentMenu)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                            <span
                                                className="me-2"
                                                style={{ fontSize: "1.2rem", cursor: "pointer" }}
                                                onClick={() => toggleExpand(parentMenu.id)}
                                            >
                                                {expandedParents.includes(parentMenu.id) ? "▼" : "▶"}
                                            </span>
                                            <strong
                                                style={{
                                                    color:
                                                        isParentSelected && !areAllChildrenSelected
                                                            ? "#999" // partial selection color
                                                            : areAllChildrenSelected
                                                                ? "#333"
                                                                : "#000",
                                                }}
                                            >
                                                {parentMenu.menuName}
                                            </strong>
                                        </div>

                                        {expandedParents.includes(parentMenu.id) &&
                                            childMenus.length > 0 && (
                                                <div style={{ paddingLeft: "2rem" }}>
                                                    {childMenus.map((child) => {
                                                        const isChildSelected = selectedMenus.includes(
                                                            String(child.id)
                                                        );
                                                        return (
                                                            <div
                                                                key={child.id}
                                                                className="d-flex align-items-center mb-1"
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    className="me-2"
                                                                    checked={isChildSelected}
                                                                    onChange={() => toggleMenuSelection(child.id)}
                                                                />
                                                                <strong>{child.menuName}</strong>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                    </div>
                                );
                            })}
                    </div>
                </div>

                <Button
                    type="button"
                    className="assignrole-btn"
                    onClick={assignMenusToRole}
                >
                    Submit
                </Button>
            </div>
        </DashboardLayout>
    );
}
export default RoleAssign;
